import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobot, selectRobotTelemetry } from 'store/selectors/robot'
import { ROBOT_NETWORK_STATUSES } from 'utils/constants'

import style from './RobotAdditionalInfo.module.css'

const cleaningEquipmentStateMapping: Record<number, string> = {
  0: 'MANUAL',
  1: 'TRANSPORT',
  2: 'CLEANING',
  10: 'EMERGENCY',
  255: 'ERROR',
}

const gnssStatusMapping: Record<number, string> = {
  0: 'NO FIX',
  1: 'SINGLE FIX',
  2: 'FLOAT FIX',
  3: 'FIXED FIX',
}

const RobotAdditionalInfo = () => {
  const { t } = useTranslation()
  const telemetry = useSelector(selectRobotTelemetry)
  const robot = useSelector(selectRobot)
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false)

  let equipmentStateString = '-' // Default if no match
  if (telemetry?.cleaningEquipmentState !== undefined) {
    equipmentStateString = cleaningEquipmentStateMapping[telemetry.cleaningEquipmentState] || 'UNKNOWN'
  }

  let gnssStatusString = '-' // Default if no match
  if (telemetry?.localization_info?.gnss_status !== undefined) {
    gnssStatusString = gnssStatusMapping[telemetry.localization_info.gnss_status] || 'UNKNOWN'
  }

  const isOnline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE

  if (!isOnline) {
    return null
  }

  return (
    <div>
      <div className={style.additionalInfoHeader}>
        <div>{t('robot.additional_info')}</div>
        <div className={style.showMore} onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}>
          {showAdditionalInfo ? t('robot.additional_info_hide') : t('robot.additional_info_show')}
          <ArrowSvg className={style.icon} />
        </div>
      </div>
      {showAdditionalInfo && (
        <>
          <div className={style.row}>
            <div>{t('robot.localization_status')}</div>
            <div>{telemetry?.localization_info?.localization_status ? 'LOCALIZED' : 'NOT LOCALIZED'}</div>
          </div>
          {telemetry?.localization_info && !telemetry.localization_info.localization_status && (
            <>
              <div className={style.row}>
                <div>{t('robot.gnss_status')}</div>
                <div>{gnssStatusString}</div>
              </div>
              <div className={style.row}>
                <div>{t('robot.imu_status')}</div>
                <div>{telemetry?.localization_info?.imu_bias_status ? 'CONVERGED' : 'NOT CONVERGED'}</div>
              </div>
            </>
          )}
          <div className={style.row}>
            <div>{t('robot.cleaning_equipment_state')}</div>
            <div>{equipmentStateString}</div>
          </div>
          {/* <div className={style.row}>
            <div>{t('robot.developer_key')}</div>
            <div>{telemetry?.statusCode}</div>
          </div> */}
          {telemetry?.temperatureControlUnit !== null &&
            telemetry?.temperatureControlUnit !== undefined &&
            telemetry.temperatureControlUnit > -273.0 && (
              <div className={style.row}>
                <div>{'Computing unit temperature'}</div>
                <div>{telemetry.temperatureControlUnit.toFixed(1) + '°C'}</div>
              </div>
            )}
          {telemetry?.temperatureChassis !== null &&
            telemetry?.temperatureChassis !== undefined &&
            telemetry.temperatureChassis > -273.0 && (
              <div className={style.row}>
                <div>{'Chassis temperature'}</div>
                <div>{telemetry.temperatureChassis.toFixed(1) + '°C'}</div>
              </div>
            )}
          {telemetry?.temperatureLidar !== null &&
            telemetry?.temperatureLidar !== undefined &&
            telemetry.temperatureLidar > -273.0 && (
              <div className={style.row}>
                <div>{'Lidar temperature'}</div>
                <div>{telemetry.temperatureLidar.toFixed(1) + '°C'}</div>
              </div>
            )}
          {telemetry?.temperatureExternal !== null &&
            telemetry?.temperatureExternal !== undefined &&
            telemetry.temperatureExternal > -273.0 && (
              <div className={style.row}>
                <div>{'External temperature'}</div>
                <div>{telemetry.temperatureExternal.toFixed(1) + '°C'}</div>
              </div>
            )}
          <div className={style.row}>
            <div>{'SIM card number'}</div>
            <div>{robot?.robotInfo?.sim_card_number ? robot.robotInfo.sim_card_number : '-'}</div>
          </div>
        </>
      )}
    </div>
  )
}

export default RobotAdditionalInfo
